import type { Component } from '@wix/thunderbolt-becky-types'
import { createCssCompNode } from '../cssCompNode'
import type { FeatureRefs } from '../cssFeatures.types'
import type { ImageXDefinition } from '@wix/thunderbolt-components'
import { removeHash } from '@wix/thunderbolt-catharsis'
import type { BreakpointsVariantsTo } from '../../shared.types'

export const imageXScrollEffects = createCssCompNode('imageXScrollEffects', 'imageXScrollEffects', {
	getDependencies: (component: Component, refs: FeatureRefs<'imageXScrollEffects'>) =>
		component.componentType === 'ImageX' && component.dataQuery
			? {
				imageXData: refs.dataQuery<ImageXDefinition['data']>(component.dataQuery!),
			}
			: null,
	toViewItem: (__, deps) => {
		if (!deps?.imageXData?.scopedData || deps?.imageXData?.scopedData.length === 0) {
			return null
		}

		const breakpointToStyleMap: BreakpointsVariantsTo<{
			'wow-image': { overflow: string; height?: string; 'min-height'?: string }
			'wow-image img': { height: string }
		}> = {}
		deps.imageXData.scopedData.forEach((datum) => {
			const breakpointId = (datum?.breakpoint && removeHash(datum.breakpoint)) || 'default'
			const variantsKey = ''
			const results = imageXDataToCss(datum)
			breakpointToStyleMap[breakpointId] = breakpointToStyleMap[breakpointId] || {}
			breakpointToStyleMap[breakpointId][variantsKey] = results
		})

		return {
			data: breakpointToStyleMap,
			variants: getVariantsObj(deps?.imageXData.scopedData),
		}
	},
})

const imageXDataToCss = (datum: any) => {
	if (datum?.scrollEffect === 'parallax') {
		return {
			// `min-height: 100%` is needed to trigger the intersection observer in the editor where there is no SSR image
			'wow-image': { overflow: 'visible', height: 'max-content', 'min-height': '100%' },
			'wow-image img': { height: 'auto' },
		}
	}
	if (datum?.scrollEffect === 'fixed') {
		return {
			'wow-image': { overflow: 'visible', height: 'max-content' },
			'wow-image img': { height: '100vh' },
		}
	} else {
		return {
			'wow-image': { overflow: 'clip' },
			'wow-image img': { height: '100%' },
		}
	}
}

const getVariantsObj = (data: ImageXDefinition['data']['scopedData']) => {
	return data?.reduce((acc: any, datum) => {
		if (!datum?.breakpoint) {
			return acc
		}
		acc[removeHash(datum.breakpoint)] = {
			type: 'BreakpointRange',
			id: removeHash(datum.breakpoint),
		}
		return acc
	}, {})
}
